import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/models';
import { environment } from '@env';
import { Observable, map, throwError } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { BrokerTokenStorageService } from '../auth/broker-token-storage.service';

export type AccountSecurityStepUp = 'complete' | 'totp_required';
export type AccountSecurityOperation = 'change_password' | 'disable_totp';

interface SessionResponseBody {
  claims?: Record<string, unknown>;
  challenge_name?: string;
  step_up?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccountSecurityService {
  private readonly clientId = environment.awsCognitoConfig.cognito.Auth.userPoolWebClientId;
  private readonly accountHttp: HttpClient;

  constructor(
    httpBackend: HttpBackend,
    private authService: AuthService,
    private brokerSessions: BrokerTokenStorageService
  ) {
    this.accountHttp = new HttpClient(httpBackend);
  }

  startStepUp(
    currentPassword: string,
    operation: AccountSecurityOperation
  ): Observable<AccountSecurityStepUp> {
    const username = this.accountUsername();
    if (!username) {
      return throwError(() => new Error('Account username is unavailable'));
    }

    return this.brokerSessions
      .startAccountSecurityStepUp(this.clientId, username, currentPassword, operation)
      .pipe(map(response => this.stepUpResult(response)));
  }

  completeStepUp(code: string, operation: AccountSecurityOperation): Observable<void> {
    return this.brokerSessions.completeAccountSecurityStepUp(this.clientId, code, operation).pipe(
      map(response => {
        if (this.responseBody(response)?.step_up !== 'complete') {
          throw new Error('Account security verification did not establish a grant');
        }
      })
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.accountHttp.put<void>(
      '/account/password',
      { currentPassword, newPassword },
      { withCredentials: true }
    );
  }

  terminateBrokerSession(): Observable<void> {
    const params = new HttpParams()
      .set('client_id', this.clientId)
      .set('logout_uri', environment.awsCognitoConfig.cognito.Auth.oauth.redirectSignOut)
      .set('redirect', 'false');

    return this.accountHttp.get<void>('/logout', { params, withCredentials: true });
  }

  private accountUsername(): string | null {
    const attributes = this.authService.attributes as Record<string, unknown> | undefined;
    const value =
      attributes?.['cognito:username'] ??
      attributes?.['username'] ??
      this.authService.cognitoUser?.username;

    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private stepUpResult(response: IApiResponse): AccountSecurityStepUp {
    const body = this.responseBody(response);
    if (body?.challenge_name === 'SOFTWARE_TOKEN_MFA') {
      return 'totp_required';
    }

    if (body?.step_up === 'complete') {
      return 'complete';
    }

    throw new Error('Account security verification returned an unsupported response');
  }

  private responseBody(response: IApiResponse | unknown): SessionResponseBody | null {
    const body = (response as IApiResponse)?.body ?? response;
    if (typeof body === 'string') {
      try {
        return JSON.parse(body) as SessionResponseBody;
      } catch {
        return null;
      }
    }

    return body && typeof body === 'object' ? (body as SessionResponseBody) : null;
  }
}
